import { createSafeGlobalState } from '@/helpers/createSafeGlobalState';
import { SnowplowContext } from '@/helpers/tracking/providers';

const globalSessions = createSafeGlobalState(() => ({} as Record<symbol, Session>));

interface SessionTrackerOptions {
	id: symbol;
	influenced: boolean;
}

export interface Session<T = any, S = any> {
	/** Arbitrary data to attach to the session. */
	data: T;

	/**
	 * Page name, url, or other starting point of the session.
	 * Useful for lifecycle management.
	 */
	start: S;

	/** If true, the session's contexts will be included in all events. */
	influenced: boolean;

	createdAt: number;
}

/** Get the Snowplow contexts in active influenced sessions if any. */
export function getInfluencedSessionsContexts() {
	const sessions = globalSessions();

	return Object.getOwnPropertySymbols(sessions)
		.map(key => sessions[key])
		.filter(session => session.influenced)
		.map(session => session.data)
		.flat()
		.filter(data => data instanceof SnowplowContext);
}

export function createSessionManager<T, S = any>(options: SessionTrackerOptions) {
	const id = options.id;
	const sessions = globalSessions();

	function attachSession(start: S, data: T) {
		sessions[id] = {
			data,
			start,
			createdAt: Date.now(),
			influenced: options.influenced,
		};
	}

	function killSession() {
		const prevSession = session();
		delete sessions[id];

		return prevSession;
	}

	function session(): Session<T> | null {
		return sessions[id] ?? null;
	}

	return {
		session,
		attachSession,
		killSession,
	};
}
